/**
 * Profile page with user profile and settings interface
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Button,
  TextField,
  Divider,
  Skeleton,
  Alert,
  Snackbar,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import {
  Email as EmailIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userApi, type UpdateProfileRequest } from '@/api/user';
import { FileUpload } from '@/components/FileUpload';
import { LinkedInConnection } from '@/components/LinkedInConnection';
import { ActionLimitsControl, UserStatistics } from '@/components';
import { DisconnectUnipileConfirmModal } from '@/components/DisconnectUnipileConfirmModal';
import { UserSyncJobsList } from '@/components/user';
import { ApiKeyManagement } from '@/components/ApiKeyManagement';


interface EditableField {
  name: string;
  email: string;
  password: string;
  unipile_id: string;
}

export const Profile: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize activeTab from URL params, default to 0
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam !== null) {
      const tabIndex = parseInt(tabParam, 10);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 4) {
        return tabIndex;
      }
    }
    return 0;
  });
  
  // Track if we're updating from internal state to avoid circular updates
  const isInternalUpdateRef = useRef(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [editableFields, setEditableFields] = useState<EditableField>({
    name: '',
    email: '',
    password: '',
    unipile_id: '',
  });
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [isRefreshingLinkedIn, setIsRefreshingLinkedIn] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);

  // Fetch user profile with statistics when on statistics tab
  const {
    data: user,
    isLoading: isLoadingProfile,
    error: profileError,
  } = useQuery({
    queryKey: ['user-profile', activeTab === 2],
    queryFn: () => userApi.getProfile(activeTab === 2),
  });

  // Fetch LinkedIn status
  const {
    data: linkedInStatus,
    isLoading: isLoadingLinkedIn,
  } = useQuery({
    queryKey: ['linkedin-status', user?.key],
    queryFn: () => userApi.getLinkedInStatus(user?.key),
    enabled: !!user?.key,
  });

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: userApi.updateProfile,
    onSuccess: async (_updatedUser, variables) => {
      // Invalidate and refetch to ensure button visibility updates immediately when unipile_id changes
      await queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      await queryClient.refetchQueries({ queryKey: ['user-profile'] });
      
      // If unipile_id was updated, refresh LinkedIn status from source
      if (variables.unipile_id !== undefined && variables.unipile_id !== user?.unipile_id) {
        // Invalidate LinkedIn status query to trigger refetch
        queryClient.invalidateQueries({ queryKey: ['linkedin-status', user?.key] });
        // Also trigger a refresh from source to get the latest status with new unipile_id
        if (user?.key) {
          userApi.getLinkedInStatus(user.key, true).then((refreshed) => {
            queryClient.setQueryData(['linkedin-status', user.key], refreshed);
          }).catch((error) => {
            console.error('Failed to refresh LinkedIn status after unipile_id update', error);
          });
        }
      }
      
      setToast({
        open: true,
        message: 'Profile updated successfully',
        severity: 'success',
      });
      setIsEditing(false);
    },
    onError: (error: any) => {
      setToast({
        open: true,
        message: error.response?.data?.message || 'Failed to update profile',
        severity: 'error',
      });
    },
  });

  const handleRefreshLinkedInStatus = async () => {
    if (!user?.key) return;
    setIsRefreshingLinkedIn(true);
    try {
      const refreshed = await userApi.getLinkedInStatus(user.key, true);
      queryClient.setQueryData(['linkedin-status', user.key], refreshed);
      // Also refresh profile to pick up any updated premium_features and unipile_id from DB
      // This ensures the disconnect button appears/disappears based on the latest unipile_id
      await queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      await queryClient.refetchQueries({ queryKey: ['user-profile'] });
    } catch (error) {
      console.error('Failed to refresh LinkedIn status from source', error);
    } finally {
      setIsRefreshingLinkedIn(false);
    }
  };

  // Upload profile image mutation
  const uploadImageMutation = useMutation({
    mutationFn: userApi.uploadProfileImage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      setToast({
        open: true,
        message: 'Profile image uploaded successfully',
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setToast({
        open: true,
        message: error.response?.data?.message || 'Failed to upload image',
        severity: 'error',
      });
    },
  });

  const redirectToPermanentLinkedInLink = useCallback(() => {
    if (!user?.key) {
      setToast({
        open: true,
        message: 'User profile is not loaded yet. Please try again.',
        severity: 'error',
      });
      return;
    }

    const baseUrl = `${window.location.origin}/reconnect-linkedin`;
    // When the user's Unipile ID is empty, use the generic reconnect link (no user_id).
    const reconnectUrl =
      user.unipile_id && user.key ? `${baseUrl}/${user.key}` : baseUrl;

    window.open(reconnectUrl, '_blank');
  }, [user, setToast]);

  // Disconnect Unipile account mutation
  const disconnectUnipileMutation = useMutation({
    mutationFn: () => userApi.disconnectUnipileAccount(user!.key),
    onSuccess: async () => {
      // Invalidate and refetch to ensure button visibility updates immediately
      await queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      await queryClient.refetchQueries({ queryKey: ['user-profile'] });
      queryClient.invalidateQueries({ queryKey: ['linkedin-status', user?.key] });
      setShowDisconnectModal(false);
      setToast({
        open: true,
        message: 'Unipile account disconnected successfully',
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setToast({
        open: true,
        message: error.response?.data?.message || 'Failed to disconnect Unipile account',
        severity: 'error',
      });
    },
  });

  // Create sync messages job mutation
  const createSyncJobMutation = useMutation({
    mutationFn: (data: { fromDate: string; toDate: string }) =>
      userApi.createSyncMessagesJob(user?.key, data.fromDate, data.toDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-sync-jobs', user?.key, user?.key] });
      setToast({
        open: true,
        message: 'Sync job created successfully',
        severity: 'success',
      });
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error_messages?.[0] || error.response?.data?.message || 'Failed to create sync job';
      setToast({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    },
  });

  // Create withdraw pending invites job mutation
  const createWithdrawInvitesMutation = useMutation({
    mutationFn: () => userApi.createWithdrawPendingInvitesJob(user?.key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-sync-jobs', user?.key, user?.key] });
      setToast({
        open: true,
        message: 'Withdraw pending invites job created successfully',
        severity: 'success',
      });
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error_messages?.[0] || error.response?.data?.message || 'Failed to create withdraw job';
      setToast({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    },
  });

  const handleCreateSyncJob = useCallback((fromDate: string, toDate: string) => {
    createSyncJobMutation.mutate({ fromDate, toDate });
  }, [createSyncJobMutation]);

  const handleWithdrawPendingInvites = useCallback(() => {
    createWithdrawInvitesMutation.mutate();
  }, [createWithdrawInvitesMutation]);

  // Initialize editable fields when user data loads
  useEffect(() => {
    if (user) {
      setEditableFields({
        name: user.name || '',
        email: user.email || '',
        password: '',
        unipile_id: user.unipile_id || '',
      });
    }
  }, [user]);

  const handleEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    if (user) {
      setEditableFields({
        name: user.name || '',
        email: user.email || '',
        password: '',
        unipile_id: user.unipile_id || '',
      });
    }
  }, [user]);

  const handleSave = useCallback(() => {
    const updateData: UpdateProfileRequest = {};
    
    if (editableFields.name !== (user?.name || '')) {
      updateData.name = editableFields.name;
    }
    
    if (editableFields.email !== (user?.email || '')) {
      updateData.email = editableFields.email;
    }
    
    if (editableFields.password) {
      updateData.password = editableFields.password;
    }

    if (editableFields.unipile_id !== (user?.unipile_id || '')) {
      updateData.unipile_id = editableFields.unipile_id;
    }

    if (Object.keys(updateData).length > 0) {
      updateProfileMutation.mutate(updateData);
    } else {
      setIsEditing(false);
    }
  }, [editableFields, user, updateProfileMutation]);

  const handleFieldChange = useCallback((field: keyof EditableField, value: string) => {
    setEditableFields(prev => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleFileSelect = useCallback((file: File) => {
    uploadImageMutation.mutate(file);
  }, [uploadImageMutation]);

  const handleLinkedInConnect = useCallback(() => {
    redirectToPermanentLinkedInLink();
  }, [redirectToPermanentLinkedInLink]);

  const handleLinkedInReconnect = useCallback(() => {
    redirectToPermanentLinkedInLink();
  }, [redirectToPermanentLinkedInLink]);

  const handleCloseToast = useCallback(() => {
    setToast(prev => ({ ...prev, open: false }));
  }, []);

  const getInitials = useCallback((name: string | null | undefined) => {
    if (!name || typeof name !== 'string' || name.trim() === '') {
      return 'U';
    }
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }, []);

  // Update URL when tab changes
  const updateURLTab = useCallback((tabIndex: number) => {
    isInternalUpdateRef.current = true;
    const newSearchParams = new URLSearchParams(searchParams);
    
    if (tabIndex === 0) {
      // Remove tab param if it's the default (first tab)
      newSearchParams.delete('tab');
    } else {
      newSearchParams.set('tab', tabIndex.toString());
    }
    
    setSearchParams(newSearchParams, { replace: true });
    setTimeout(() => {
      isInternalUpdateRef.current = false;
    }, 0);
  }, [searchParams, setSearchParams]);

  // Sync state from URL params when URL changes externally (e.g., browser back button)
  useEffect(() => {
    if (isInternalUpdateRef.current) {
      return;
    }

    const tabParam = searchParams.get('tab');
    let urlTabIndex = 0;
    if (tabParam !== null) {
      const parsedTab = parseInt(tabParam, 10);
      if (!isNaN(parsedTab) && parsedTab >= 0 && parsedTab <= 4) {
        urlTabIndex = parsedTab;
      }
    }

    if (urlTabIndex !== activeTab) {
      setActiveTab(urlTabIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      setActiveTab(newValue);
      updateURLTab(newValue);
    },
    [updateURLTab],
  );

  if (isLoadingProfile) {
    return (
      <Box>
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="text" width={200} height={40} />
          <Skeleton variant="text" width={300} height={24} />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' }, gap: 4 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                <Skeleton variant="circular" width={80} height={80} />
                <Skeleton variant="text" width={150} height={32} />
                <Skeleton variant="text" width={100} height={24} />
              </Box>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Skeleton variant="text" width={200} height={32} />
              <Skeleton variant="text" width="100%" height={56} sx={{ mt: 2 }} />
              <Skeleton variant="text" width="100%" height={56} sx={{ mt: 2 }} />
              <Skeleton variant="text" width="100%" height={56} sx={{ mt: 2 }} />
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  }

  if (profileError) {
    return (
      <Box>
        <Alert severity="error">
          Failed to load profile: {profileError.message}
        </Alert>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box>
        <Alert severity="error">
          User profile not found
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{my: 2, mx: 2}}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          Profile
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your personal information and account settings.
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' }, gap: 4 }}>
        {/* Left Column */}
        <Box>
          {/* Profile Card */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                <Avatar
                  src={user.profile_image || undefined}
                  sx={{ width: 80, height: 80, bgcolor: 'primary.main', mb: 2, fontSize: 36 }}
                >
                  {getInitials(user.name)}
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {user.name?.trim() || 'User'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user.email || 'No email'}
                </Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              
              {/* Profile Image Upload */}
              <FileUpload
                onFileSelect={handleFileSelect}
                currentImageUrl={user.profile_image || undefined}
                isLoading={uploadImageMutation.isPending}
              />
            </CardContent>
          </Card>
        </Box>

        {/* Settings & Limits Tabs */}
        <Card>
          <CardContent>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{ mb: 2 }}
            >
              <Tab label="Account Settings" />
              <Tab label="Limits & Activity Control" />
              <Tab label="Statistics" />
              <Tab label="Actions" />
              <Tab label="API Key" />
            </Tabs>

            {activeTab === 0 && (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Account Settings
                  </Typography>
                  {!isEditing ? (
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={handleEdit}
                    >
                      Edit Profile
                    </Button>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={updateProfileMutation.isPending}
                      >
                        {updateProfileMutation.isPending ? (
                          <CircularProgress size={20} />
                        ) : (
                          'Save'
                        )}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<CancelIcon />}
                        onClick={handleCancel}
                        disabled={updateProfileMutation.isPending}
                      >
                        Cancel
                      </Button>
                    </Box>
                  )}
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 2,
                  }}
                >
                  <TextField
                    label="Full Name"
                    value={isEditing ? editableFields.name : (user.name || '')}
                    onChange={(e) => handleFieldChange('name', e.target.value)}
                    disabled={!isEditing}
                    fullWidth
                    margin="normal"
                    InputProps={{
                      startAdornment: (
                        <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      ),
                    }}
                  />
                  <TextField
                    label="Email"
                    value={isEditing ? editableFields.email : (user.email || '')}
                    onChange={(e) =>
                      handleFieldChange('email', e.target.value)
                    }
                    disabled={!isEditing}
                    fullWidth
                    margin="normal"
                    InputProps={{
                      startAdornment: (
                        <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      ),
                    }}
                  />
                  <TextField
                    label="Unipile ID"
                    value={
                      isEditing
                        ? editableFields.unipile_id
                        : (user.unipile_id || '')
                    }
                    onChange={(e) =>
                      handleFieldChange('unipile_id', e.target.value)
                    }
                    disabled={!isEditing}
                    fullWidth
                    margin="normal"
                    InputProps={{
                      startAdornment: (
                        <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      ),
                    }}
                    helperText="Your Unipile account identifier"
                  />
                  {isEditing && (
                    <TextField
                      label="New Password"
                      type={showPassword ? 'text' : 'password'}
                      value={editableFields.password}
                      onChange={(e) =>
                        handleFieldChange('password', e.target.value)
                      }
                      fullWidth
                      margin="normal"
                      InputProps={{
                        startAdornment: (
                          <LockIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        ),
                        endAdornment: (
                          <Button
                            onClick={() => setShowPassword(!showPassword)}
                            sx={{ minWidth: 'auto', p: 0.5 }}
                          >
                            {showPassword ? (
                              <VisibilityOffIcon />
                            ) : (
                              <VisibilityIcon />
                            )}
                          </Button>
                        ),
                      }}
                      helperText="Leave blank to keep current password"
                    />
                  )}
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* LinkedIn Connection */}
                <LinkedInConnection
                  status={linkedInStatus || null}
                  isLoading={isLoadingLinkedIn}
                  onConnect={handleLinkedInConnect}
                  onReconnect={handleLinkedInReconnect}
                  onRefresh={handleRefreshLinkedInStatus}
                  isRefreshing={isRefreshingLinkedIn}
                userId={user.key}
                hasUnipileId={!!user.unipile_id}
                />

                {/* Disconnect Button - Only show if user has unipile_id AND account is connected */}
                {user?.unipile_id && linkedInStatus?.is_connected && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => setShowDisconnectModal(true)}
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      Disconnect Unipile Account
                    </Button>
                  </Box>
                )}
              </>
            )}

            {activeTab === 1 && (
              <Box sx={{ mt: 1 }}>
                <ActionLimitsControl scope="user" resourceId={user.key} />
              </Box>
            )}

            {activeTab === 2 && (
              <Box sx={{ mt: 1 }}>
                <UserStatistics user={user as any} />
              </Box>
            )}

            {activeTab === 3 && (
              <Box sx={{ mt: 1 }}>
                <UserSyncJobsList
                  userUuid={user?.key}
                  isConnected={linkedInStatus?.is_connected || false}
                  hasUnipileId={!!user?.unipile_id}
                  onCreateJob={handleCreateSyncJob}
                  onWithdrawPendingInvites={handleWithdrawPendingInvites}
                />
              </Box>
            )}

            {activeTab === 4 && (
              <Box sx={{ mt: 1 }}>
                <ApiKeyManagement />
              </Box>
            )}
          </CardContent>
        </Card>
        </Box>

      {/* Toast Notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseToast}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      {/* Disconnect Confirmation Modal */}
      <DisconnectUnipileConfirmModal
        open={showDisconnectModal}
        onClose={() => setShowDisconnectModal(false)}
        onConfirm={() => disconnectUnipileMutation.mutate()}
        isDisconnecting={disconnectUnipileMutation.isPending}
        userName={user?.name || undefined}
      />
    </Box>
  );
}; 